/**
 * Per-chapter resume positions in localStorage. Keyed by `${book}.${chapter}`
 * so each Bible-book/chapter remembers where the user stopped listening.
 *
 * We store seconds rather than verseRef so resume snaps back to the exact
 * audio position even if the chapter's verse cues drift between fileset
 * variants (plain vs dramatized differ by a few hundred ms per verse).
 */

const STORAGE_KEY = '_rs1';
const MAX_ENTRIES = 200;
const MIN_RESUME_SEC = 8;

type ResumeMap = Record<string, { sec: number; updatedAt: number }>;

function read(): ResumeMap {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as ResumeMap;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function write(map: ResumeMap): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const entries = Object.entries(map);
    if (entries.length > MAX_ENTRIES) {
      entries.sort((a, b) => b[1].updatedAt - a[1].updatedAt);
      const trimmed: ResumeMap = {};
      for (let i = 0; i < MAX_ENTRIES; i++) {
        const entry = entries[i];
        if (entry) trimmed[entry[0]] = entry[1];
      }
      map = trimmed;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* quota or private mode — ignore */
  }
}

export function resumeKey(book: string, chapter: number, bibleId?: string): string {
  const ref = `${book.toUpperCase()}.${chapter}`;
  return bibleId ? `${bibleId.toUpperCase()}:${ref}` : ref;
}

export function getResume(book: string, chapter: number, bibleId?: string): number {
  const map = read();
  // Prefer the bible-namespaced entry; fall back to the legacy un-namespaced
  // key so users with existing saved positions don't lose them.
  const entry = map[resumeKey(book, chapter, bibleId)] ?? map[resumeKey(book, chapter)];
  return entry?.sec ?? 0;
}

export function setResume(book: string, chapter: number, sec: number, bibleId?: string): void {
  if (!Number.isFinite(sec) || sec < MIN_RESUME_SEC) return;
  const map = read();
  map[resumeKey(book, chapter, bibleId)] = { sec, updatedAt: Date.now() };
  write(map);
}

export function clearResume(book: string, chapter: number, bibleId?: string): void {
  const map = read();
  delete map[resumeKey(book, chapter, bibleId)];
  write(map);
}
